import { FC } from 'react';
import { Grid } from '@mui/material';
import { FieldRow, FieldLabel, Panel } from 'src/generic/component';
import { Checkbox, TextArea } from 'src/generic/component/form';
import {  CustomAddressFields } from 'src/containers/school/ai/components/CustomAddressFields';
import { Draft } from 'src/types/common';
import { IAIRequest } from 'src/types/org/ai/schoolInterface';
import { IFieldErrors, ErrorManager } from 'src/types/validation';
import { IAddress } from 'src/types/address';

interface IAddressPanelProps {
  item: Draft<IAIRequest>;
  readOnly?: boolean;
  errors: IFieldErrors;
  onUpdate: (updates: Draft<IAIRequest>) => void;
}

export const AddressPanel: FC<IAddressPanelProps> = ({ item, readOnly, errors, onUpdate }) => {
  const { getError, getFieldError, hasError } = ErrorManager(errors);

  const org = item.data?.org;
  const appForm = item.data?.appForm;
  const { visitingAddress, shippingAddress, shippingPolicyException } = org || {};

  const changed = (updates: Partial<{
    org?: Partial<typeof org>;
    appForm?: Partial<typeof appForm>;
  }>) => {
    const updatedItem: Draft<IAIRequest> = {
      ...item,
      data: {
        ...item.data,
        org: {
          ...org,
          ...updates.org,
        },
        appForm: { ...appForm, ...updates.appForm }
      }
    };
    onUpdate(updatedItem);
  };

  return (
    <Panel>
      <Grid container spacing={5}>
        <Grid item xs={6}>
          <FieldRow><strong>Physical Address</strong>(Location where students report for in-person instruction)</FieldRow>
          <CustomAddressFields
            readOnly={readOnly}
            errors={getFieldError('data.org.visitingAddress')}
            address={visitingAddress}
            onUpdate={(address) => {
              changed({ org: { visitingAddress: address } });
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <FieldRow>
            <Checkbox
              disabled={readOnly}
              label={<><strong>Shipping Address</strong> (if different from physical address)</>}
              style={{ display: 'inline-block' }}
              checked={shippingAddress ? true : false}
              onChange={() => {
                if (!shippingAddress) {
                  changed({
                    org: {
                      shippingAddress: {
                        countryIsoCd: visitingAddress?.countryIsoCd || 'US',
                        stateCd: visitingAddress?.stateCd,
                        province: visitingAddress?.province
                      }
                    }
                  });
                } else {
                  changed({
                    org: {
                      shippingAddress: undefined,
                      shippingPolicyException: undefined
                    }
                  });
                }
              }}
            />
            {hasError('data.org.shippingAddress') &&
              <span className='cb-input-helper cb-validation-error'>{getError('data.org.shippingAddress')}</span>
            }
          </FieldRow>
          {shippingAddress &&
            <CustomAddressFields
              readOnly={readOnly}
              errors={getFieldError('data.org.shippingAddress')}
              address={shippingAddress}
              onUpdate={(address: Draft<IAddress>) => {
                changed({ org: { shippingAddress: address } });
              }}
              additionalInfo={true}
              customLabels={{
                label: 'Alternate Shipping Institution Name',
                contactName: 'Shipping Contact Name',
                contactPhone: 'Shipping Contact Phone'
              }}
            />
          }
          {shippingAddress && (
            <FieldRow>
              <FieldLabel variant="body1" style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                College Board assessment administration policy states materials must be shipped, stored and administered at the same location. If adding an alternate shipping address, please explain why your organization needs to request a policy exception.
                <br />
                <br />
                <strong>Please note:</strong> Applications with policy exception requests will incur extended processing time.
              </FieldLabel>
              <TextArea
                disabled={readOnly}
                validation={hasError('data.org.shippingPolicyException') ? 'error' : undefined}
                validationMsg={getError('data.org.shippingPolicyException')}
                onChange={(e) => {
                  changed({ org: { shippingPolicyException: e.target.value } });
                }}
                value={shippingPolicyException || ''}
              />
            </FieldRow>
          )}
        </Grid>
      </Grid>
    </Panel>
  );
};